/* global module*/
import React from 'react';
import flatten from 'lodash/flatten';
import includes from 'lodash/includes';
import get from 'lodash/get';
import pick from 'lodash/pick';
import cx from 'classnames';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import ExternalOffset from './external_offset/external_offset.component';
import Action from './action/action.component';
import Filters from './filters';
import { withT, wrap, withRouterAndHelpers } from '../../../lib/wrappers';
import s from '../../../redux/selectors';
import a from '../../../redux/actions';
import actionHelpers from '../../../lib/actions';
import { userApiValue, userApiState } from '../../../lib/legacyHelpers';
import ResizableComponent from '../../../lib/base_classes/ResizableComponent';
import NavButtons from '../../common/NavButtons.tsx';
import { userPledgeStatus } from '../../../lib/userApi';

// eslint-disable-next-line import/prefer-default-export
export const ACTIONS = [{
  category: 'transportation',
  title: 'Transportation',
  keys: ['more_efficient_vehicle', 'alternativefuel_vehicle', 'electric_vehicle', 'hybrid_vehicle', 'telecommute_to_work', 'ride_my_bike', 'take_public_transportation', 'practice_eco_driving', 'maintain_my_vehicles', 'carpool_to_work', 'reduce_air_travel'],
}, {
  category: 'housing',
  title: 'Housing',
  keys: ['switch_to_cfl', 'turn_off_lights', 'T12toT8', 'tankless_water_heater', 'thermostat_winter', 'thermostat_summer', 'purchase_high_efficiency_cooling', 'purchase_high_efficiency_heating', 'energy_star_fridge', 'energy_star_printers', 'energy_star_copiers', 'energy_star_desktops', 'rechargeable_batteries', 'power_mgmt_comp', 'purchase_green_electricity', 'install_PV_panels', 'install_solar_heating', 'low_flow_showerheads', 'low_flow_faucets', 'low_flow_toilet', 'line_dry_clothing', 'water_efficient_landscaping', 'plant_trees', 'reduce_comm_waste', 'print_double_sided'],
}, {
  category: 'shopping',
  title: 'Shopping',
  keys: ['low_carbon_diet', 'go_organic'],
}];

const getActionStatusFromFilterValue = (filterValue) => {
  switch (filterValue) {
    case 'completed':
      return userPledgeStatus.COMPLETED;
    case 'pledged':
      return userPledgeStatus.PLEDGED;
    case 'not_relevant':
      return userPledgeStatus.NOT_RELEVANT;
    default:
      throw new Error(`Unexpected status filter ${filterValue}.`);
  }
};

class TakeActionComponent extends ResizableComponent {

  constructor(props, context) {
    super(props, context);
    const take_action = this;
    take_action.action_keys = Object.keys(take_action.result_takeaction_pounds)
      .filter(key => !/^offset_/.test(key));
    take_action.state = userApiState(
      props.user_footprint,
      props.average_footprint,
      this.relevant_api_keys,
    );
    take_action.state.input_action_category = ACTIONS[0].category;
    take_action.state.show_actions = ACTIONS[0].keys;
    take_action.state.show_critical_assumptions = false;
    this.state.show_filters = false;
  }

  componentDidMount() {
    this.setVehicles();
  }

  get t() {
    return this.props.t;
  }

  get external_offset_set() {
    const offset = this.props.ui.external_offset;
    return Object.keys(offset).length !== 0;
  }

  get actions_list() {
    return ACTIONS;
  }

  get category_keys() {
    return flatten(
      ACTIONS.map(category => category.keys.map(key => [category.category, key])));
  }

  get relevant_api_keys() {
    return this.action_keys;
  }

  get total_user_footprint() {
    return get(this.props.user_footprint, 'compute_takeaction_results.result_takeaction_grand_total');
  }

  get showActions() {
    return !get(this.props.ui, 'widget.options.cta.hideActions', false);
  }


  get result_takeaction_pounds() {
    return this.props.resultTakeActionTons;
  }

  get result_takeaction_dollars() {
    return this.props.resultTakeActionDollars;
  }

  get result_takeaction_net10yr() {
    return this.props.resultTakeActionNet10Yr;
  }

  get show_critical_assumptions() {
    return this.state.show_critical_assumptions;
  }

  toggleFilterVisibility() {
    this.setState({
      show_filters: !this.state.show_filters,
    });
  }

  setVehicles() {
    const { user_footprint, average_footprint } = this.props;
    const num = userApiValue('input_footprint_transportation_num_vehicles', user_footprint, average_footprint);
    const vehicles = [];

    // eslint-disable-next-line no-plusplus
    for (let i = 1; i <= num; i++) {
      const vehicle = {};
      vehicle.miles = userApiValue(`input_footprint_transportation_miles${i}`, user_footprint, average_footprint);
      vehicle.mpg = userApiValue(`input_footprint_transportation_mpg${i}`, user_footprint, average_footprint);
      vehicles.push(vehicle);
    }

    this.props.updateUI({ id: 'vehicles', data: vehicles });
  }

  isCategoryActive(category) {
    return this.state.input_action_category === category;
  }

  toggleCriticalAssumptions() {
    this.setState({
      show_critical_assumptions: !this.state.show_critical_assumptions,
    });
  }

  /*
   * Filtering and Sorting Actions
   */

  get all_actions() {
    return flatten(this.actions_by_category.map(category => category.keys))
      .filter(key => !/^offset_/.test(key));
  }

  get filters() {
    return get(this.props.ui, 'take_action.filters', {});
  }

  filter(filters) {
    this.props.updateUI({
      id: 'take_action',
      data: {
        filters,
      },
    });
  }

  sort(attribute) {
    const sort_by = get(this.props.ui, 'take_action.sort_by');
    const data = {
      sort_by: attribute,
    };
    if (attribute === sort_by) {
      const sort_reverse = get(this.props.ui, 'take_action.sort_reverse');
      data.sort_reverse = !sort_reverse;
    }
    this.props.updateUI({
      id: 'take_action',
      data,
    });
  }

  get sorted_filtered_actions() {
    const { filters } = this;
    const { pledges } = this.props;

    // category
    let actions = !filters.category ?
      this.all_actions : this.getActionsByCategory(filters.category);

    // status
    if (filters.status === 'not_pledged') {
      actions = actions
        .filter(actionKey =>
          !pledges[actionKey] || pledges[actionKey] === userPledgeStatus.NOT_RELEVANT,
        );
    } else if (filters.status) {
      const actionStatus = getActionStatusFromFilterValue(filters.status);
      actions = actions
        .filter(actionKey => pledges[actionKey] === actionStatus);
    }

    if (get(this.props.ui, 'widget.options.source') === 'ontario') {
      actions = actions.filter(key => !includes([
        'alternativefuel_vehicle',
        'install_solar_heating',
        'purchase_green_electricity',
        'go_organic',
      ], key));
    }

    return this.sortActions(actions);
  }

  get sort_by() {
    return get(this.props.ui, 'take_action.sort_by');
  }

  get sort_reverse() {
    return get(this.props.ui, 'take_action.sort_reverse');
  }

  get actions_by_category() {
    return [{
      id: 'transportation',
      keys: ['more_efficient_vehicle', 'alternativefuel_vehicle', 'electric_vehicle', 'hybrid_vehicle', 'telecommute_to_work', 'ride_my_bike', 'take_public_transportation', 'practice_eco_driving', 'maintain_my_vehicles', 'carpool_to_work', 'reduce_air_travel', 'offset_transportation'],
    }, {
      id: 'housing',
      keys: ['switch_to_cfl', 'turn_off_lights', 'T12toT8', 'tankless_water_heater', 'thermostat_winter', 'thermostat_summer', 'purchase_high_efficiency_cooling', 'purchase_high_efficiency_heating', 'energy_star_fridge', 'energy_star_printers', 'energy_star_copiers', 'energy_star_desktops', 'rechargeable_batteries', 'power_mgmt_comp', 'purchase_green_electricity', 'install_PV_panels', 'install_solar_heating', 'low_flow_showerheads', 'low_flow_faucets', 'low_flow_toilet', 'line_dry_clothing', 'water_efficient_landscaping', 'plant_trees', 'reduce_comm_waste', 'print_double_sided', 'offset_housing'],
    }, {
      id: 'food',
      keys: ['low_carbon_diet', 'go_organic', 'offset_shopping'],
    }];
  }

  sortActions(actions_to_sort) {
    const { sort_by } = this;

    if (!sort_by) return actions_to_sort;

    const { sort_reverse } = this;
    if (sort_by === 'title') {
      actions_to_sort.sort((item1, item2) => {
        const aLabel = actionHelpers.getLabel(item1, this.t);
        const bLabel = actionHelpers.getLabel(item2, this.t);

        if (aLabel < bLabel) return sort_reverse ? 1 : -1;
        if (aLabel > bLabel) return sort_reverse ? -1 : 1;
        return 0;
      });
    } else if (sort_by === 'tons_saved') {
      actions_to_sort.sort((item1, item2) => {
        const aSavings = this.getTonsSavedByAction(item1);
        const bSavings = this.getTonsSavedByAction(item2);

        if (aSavings > bSavings) return sort_reverse ? 1 : -1;
        if (aSavings < bSavings) return sort_reverse ? -1 : 1;
        return 0;
      });
    } else if (sort_by === 'dollars_saved') {
      actions_to_sort.sort((item1, item2) => {
        const aSavings = this.getDollarsSavedByAction(item1);
        const bSavings = this.getDollarsSavedByAction(item2);

        if (aSavings > bSavings) return sort_reverse ? 1 : -1;
        if (aSavings < bSavings) return sort_reverse ? -1 : 1;
        return 0;
      });
    } else if (sort_by === 'upfront_cost') {
      actions_to_sort.sort((item1, item2) => {
        const aCost = this.getUpfrontCostByAction(item1);
        const bCost = this.getUpfrontCostByAction(item2);

        if (aCost < bCost) return sort_reverse ? 1 : -1;
        if (aCost > bCost) return sort_reverse ? -1 : 1;
        return 0;
      });
    }
    return actions_to_sort;
  }

  getActionsByCategory(category) {
    return this.actions_by_category
      .find(group => group.id === category)
      .keys
      .filter(key => !/^offset_/.test(key));
  }

  actionCategory(action_key) {
    return this.actions_by_category.find(group => includes(group.keys, action_key)).id;
  }

  getTonsSavedByAction(action_key) {
    return this.result_takeaction_pounds[action_key] * 100;
  }

  getDollarsSavedByAction(action_key) {
    return this.result_takeaction_dollars[action_key];
  }

  getUpfrontCostByAction(action_key) {
    return this.result_takeaction_net10yr[action_key];
  }

  render() {
    return (
      <div className="cc-component" id="take_action">
        <header className="cc-component__header">
          {!this.external_offset_set && <span className="cc-component__byline">{this.t('Reduce your impact')}</span>}
          {this.props.widgetSource === 'ontario' &&
            <p>
              {this.t('For the latest updates and information on greenhouse gas reduction and energy efficiency programs in Ontario visit')}
              <a
                href={`https://${this.t('greenon.ca/')}`}
                target="_blank"
                rel="noopener noreferrer"
              >{this.t('the Green Ontario Fund')}</a>
              {this.t('and')}
              <a
                href={`https://${this.t('greenon.ca/')}subscription`}
                target="_blank"
                rel="noopener noreferrer"
              >{this.t('subscribe')}</a>
              {this.t('to e-alerts.')}
            </p>
          }
        </header>

        {this.external_offset_set &&
          <ExternalOffset
            totalUserFootprint={this.total_user_footprint}
            ui={this.props.ui}
            widgetSource={this.props.widgetSource}
          />
        }
        {this.showActions &&
          <div className="cc__actions">
            <div style={{ marginBottom: '10px' }} className="take_action__filter-toggle">
              <a
                id="take_action__filter_toggle"
                type="button"
                onClick={e => this.toggleFilterVisibility(e)}
              >
                <i
                  className={cx('fa', { 'fa-chevron-down': !this.state.show_filters, 'fa-chevron-up': this.state.show_filters })}
                />
                {this.t('Show Filters')}
              </a>
            </div>
            <Filters
              visible={this.state.show_filters}
              filters={this.filters}
              filter={filters => this.filter(filters)}
            />

            <div
              className="cc-component__form"
              style={{ maxHeight: '500px', overflowY: 'auto', overflowX: 'hidden' }}
            >
              <table className="table action__list-table">
                <thead>
                  <tr>
                    <td />
                    <th
                      id="take_action__sort--title"
                      className="take_action__list-header take_action__sortable-header"
                      onClick={() => this.sort('title')}
                    >
                      <div
                        className={cx({ 'take_action__sortable-header__no-sort': this.sort_by !== 'title' })}
                      >
                        {this.sort_by === 'title' &&
                          <i
                            className={cx({ fa: true, 'fa-arrow-down': !this.sort_reverse, 'fa-arrow-up': this.sort_reverse })}
                          />
                        }
                        <span>{this.t('Name')}</span>
                      </div>
                    </th>
                    <th
                      id="take_action__sort--tons_saved"
                      className="take_action__list-header take_action__sortable-header"
                      onClick={() => this.sort('tons_saved')}
                    >
                      <div className={cx({ 'take_action__sortable-header__no-sort': this.sort_by !== 'tons_saved' })}>
                        {this.sort_by === 'tons_saved' &&
                          <i
                            className={cx({ fa: true, 'fa-arrow-down': this.sort_reverse, 'fa-arrow-up': !this.sort_reverse })}
                          />
                        }
                        <span>{this.t('Tons saved')}</span>
                      </div>
                    </th>
                    <th
                      id="take_action__sort--dollars_saved"
                      className="take_action__list-header take_action__sortable-header"
                      onClick={() => this.sort('dollars_saved')}
                    >
                      <div className={cx({ 'take_action__sortable-header__no-sort': this.sort_by !== 'dollars_saved' })}>
                        {this.sort_by === 'dollars_saved' &&
                          <i
                            className={cx({ fa: true, 'fa-arrow-down': this.sort_reverse, 'fa-arrow-up': !this.sort_reverse })}
                          />
                        }
                        <span>{this.t('Dollars saved / year')}</span>
                      </div>
                    </th>
                    <th
                      id="take_action__sort--upfront_cost"
                      className="take_action__list-header take_action__sortable-header"
                      onClick={() => this.sort('upfront_cost')}
                    >
                      <div className={cx({ 'take_action__sortable-header__no-sort': this.sort_by !== 'upfront_cost' })}>
                        {this.sort_by === 'upfront_cost' &&
                          <i
                            className={cx({ fa: true, 'fa-arrow-down': !this.sort_reverse, 'fa-arrow-up': this.sort_reverse })}
                          />
                        }
                        <span>{this.t('Upfront cost')}</span>
                      </div>
                    </th>
                    <th className="take_action__list-header">{this.t('Status')}</th>
                  </tr>
                </thead>
                {this.sorted_filtered_actions.map((action_key, index) => (
                  <Action
                    action_key={action_key}
                    position={index + 1}
                    key={action_key}
                    category={this.actionCategory(action_key)}
                    is_assumption={false}
                  />
                ))}
              </table>
            </div>
          </div>
          }
        <NavButtons ui={this.props.ui} />
      </div>
    );
  }

}

const mapStateToProps = (state, ownProps) => [
  'resultTakeActionTons',
  'resultTakeActionDollars',
  'resultTakeActionNet10Yr',
  'totalTonsSaved',
  'totalDollarsSaved',
  'totalNet10YrCost',
  'user_footprint',
  'average_footprint',
  'ui',
  'widgetSource',
  'pledges',
].reduce((props, selector) => ({
  ...props,
  [selector]: s[selector](state, ownProps),
}), {});

const mapDispatchToProps = dispatch => bindActionCreators(
  pick(a, [
    'updateUI',
  ]),
  dispatch,
);

export default wrap(
  withRouterAndHelpers,
  withT('takeAction'),
  connect(mapStateToProps, mapDispatchToProps),
  TakeActionComponent,
);
